// useVersionCheck.js
import axios from 'axios';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const DEBOUNCE_MS = 30 * 1000; // avoid stacking focus + poll + nav firing together

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const lastCheckRef = useRef(0);
  const updateAvailableRef = useRef(false); // avoids stale closure inside checkVersion
  const location = useLocation();
  const isFirstRender = useRef(true);

  const checkVersion = useCallback(async () => {
    if (updateAvailableRef.current) return; // already known, stop polling for more
    if (Date.now() - lastCheckRef.current < DEBOUNCE_MS) return;
    lastCheckRef.current = Date.now();

    try {
      const res = await axios.get('/version.json', {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });
      const { version, buildId } = await res.data;

      if (version !== VERSION || buildId !== BUILD_ID) {
        updateAvailableRef.current = true;
        setUpdateAvailable(true);
      }
    } catch {
      // network error — don't flag an update, just retry on next trigger
    }
  }, []);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Trigger 1 & 2: interval poll + visibility/focus
  useEffect(() => {
    checkVersion(); // on mount
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') checkVersion();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkVersion]);

  // Trigger 3: route change — also the safe moment to auto-reload
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    checkVersion();
    if (updateAvailableRef.current) {
      reload();
    }
  }, [location.pathname, checkVersion, reload]);

  return {
    updateAvailable: updateAvailable && !dismissed,
    dismiss,
    reload,
  };
}
